/* Linden — the "buy it with" block.

   In an asset, not in the snippet's {% javascript %} block. Shopify compiles
   those into a shared bundle that is emitted only when the snippet renders in
   the initial response, and that is served from cache after a push — the same
   arrangement left quantity-selector undefined on any page loaded with an empty
   cart, and then served a stale copy of this component for a good while after
   it had been fixed. Behaviour that a live shop depends on should arrive by a
   versioned asset URL, like drawer.js and cart.js always have.
*/
/* Complementary products, in one of two modes set per block.

   Deferred — the original — builds a selection that cart.js posts alongside
   the main product in a single request, so a bundle is never half added.

   Instant puts the item in the cart the moment its box is ticked, and takes
   it back out when the box is cleared. A checkbox sitting next to a price
   reads as a control, not as a note for later, and a customer who ticks three
   boxes and walks away expecting them to be in the cart is not being unclear
   about what they meant.

   Instant mode makes the box a mirror of the cart, so it reconciles against
   /cart.js on connect. That reconciliation is client-side on purpose: ticked
   markup must never be what the server sends, both because a cached page
   would then lie and because Article 22 is precisely about defaults the
   consumer has to reject. */
class BuyItWith extends HTMLElement {
  connectedCallback() {
    this.instant = this.dataset.instant === 'true';
    this.total = this.querySelector('[data-bundle-total]');
    this.totalValue = this.querySelector('[data-bundle-total-value]');
    this.errorBox = this.querySelector('[data-bundle-error]');

    /* Delegated to the element. Rows come back from the section rendering API
       on a cart change, and a listener bound to a box would not survive it. */
    this.addEventListener('change', (event) => this.onChange(event));

    this.refresh();
    if (this.instant) this.mirrorCart();
  }

  get root() {
    return window.Shopify?.routes?.root || '/';
  }

  /* Read by cart.js on submit. Empty in instant mode: those lines are already
     in the cart, and posting them again would double every ticked item. */
  get selection() {
    if (this.instant) return [];

    return Array.from(this.querySelectorAll('[data-bundle-item]:checked')).map((box) => ({
      id: Number(this.variantIdFor(box)),
      quantity: 1,
    }));
  }

  onChange(event) {
    const box = event.target.closest('[data-bundle-item]');
    const select = event.target.closest('[data-bundle-variant-for]');

    if (this.instant && box) {
      this.sync(box);
      return;
    }

    if (this.instant && select) {
      const owner = this.querySelector(`#${CSS.escape(select.dataset.bundleVariantFor)}`);
      if (owner?.checked) this.swap(owner);
      return;
    }

    this.refresh();
  }

  /* Ticks the boxes whose variant is already in the cart, so the control tells
     the truth on a revisit or a back-button return. */
  async mirrorCart() {
    try {
      const response = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;

      const held = new Set((await response.json()).items.map((item) => String(item.variant_id)));

      for (const box of this.querySelectorAll('[data-bundle-item]')) {
        const id = String(this.variantIdFor(box));
        if (held.has(id)) {
          box.checked = true;
          box.dataset.inCart = id;
          this.mark(box, true);
        }
      }
    } catch (error) {
      // A box left unticked is the safe way to be wrong: it adds nothing.
    }
  }

  /* Serialised, like every other cart mutation in the theme. Two concurrent
     POSTs to the cart conflict: one comes back reporting success while its
     line never lands. Ticking two boxes quickly left both rows saying "Dans
     le panier" over a cart holding one of them — the row lying about the cart
     is the one thing this control must never do. */
  enqueue(job) {
    this.chain = (this.chain || Promise.resolve()).then(job, job);
    return this.chain;
  }

  sync(box) {
    const wanted = box.checked;
    this.busy(box, true);

    return this.enqueue(async () => {
    try {
      if (wanted) {
        const id = this.variantIdFor(box);
        await this.add(id);
        box.dataset.inCart = id;
        this.mark(box, true);
        this.reveal(box);
      } else {
        await this.drop(box.dataset.inCart || this.variantIdFor(box));
        delete box.dataset.inCart;
        this.mark(box, false);
      }

      this.say('');
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (error) {
      /* The cart did not change, so neither the box nor the row may claim
         otherwise: a failed add leaves it out, a failed removal leaves it in.
         Both are !wanted. */
      box.checked = !wanted;
      this.mark(box, !wanted);
      this.say(error.message);
    } finally {
      this.busy(box, false);
      this.refresh();
    }
    });
  }

  /* A variant change on an already-ticked row swaps one line for the other
     rather than leaving the old colour in the cart. */
  swap(box) {
    const previous = box.dataset.inCart;
    const next = this.variantIdFor(box);
    if (!previous || String(previous) === String(next)) return;

    this.busy(box, true);
    return this.enqueue(async () => {
    try {
      await this.drop(previous);
      await this.add(next);
      box.dataset.inCart = next;
      this.say('');
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (error) {
      this.say(error.message);
    } finally {
      this.busy(box, false);
    }
    });
  }

  async add(id) {
    const response = await fetch(`${this.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.description || data.message || '');
    }
  }

  /* change.js takes a variant id as well as a line key, and a complementary
     product appears at most once, so the variant id is enough here. */
  async drop(id) {
    const response = await fetch(`${this.root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: String(id), quantity: 0 }),
    });

    if (!response.ok) throw new Error('');
  }

  /* Adding a product from anywhere else opens the drawer, and a tick here is
     an add like any other — the line of text under the price turned out to be
     too quiet to read as one, which is what both of them reported.

     Debounced, so ticking three boxes in a row opens the drawer once at the
     end rather than three times over the block being ticked. cart.js decides
     whether it opens at all: if the merchant has turned auto-open off, an
     upsell has no business overriding that. */
  /* A tick needs an answer — the line of text under the price was too quiet to
     read as one. Opening the cart drawer was too loud in the other direction:
     it covers the very block being ticked, which is fine once and wrong three
     times in a row. A toast says it without taking the page away. */
  reveal(box) {
    const template = this.dataset.toast;
    if (!template) return;

    const title = box
      .closest('.bundle__item')
      ?.querySelector('.bundle__title')
      ?.textContent.trim();

    if (!title) return;

    document.dispatchEvent(new CustomEvent('toast', {
      detail: {
        message: template.replace('@@', title),
        action: this.dataset.toastAction && this.dataset.cartUrl
          ? { label: this.dataset.toastAction, href: this.dataset.cartUrl }
          : null,
      },
    }));
  }

  mark(box, inCart) {
    const state = box.closest('.bundle__item')?.querySelector('[data-bundle-state]');
    if (state) state.hidden = !inCart;
  }

  busy(box, state) {
    box.disabled = state;
    box.closest('.bundle__item')?.classList.toggle('bundle__item--busy', state);
  }

  say(message) {
    if (!this.errorBox) return;
    this.errorBox.textContent = message || '';
    this.errorBox.hidden = !message;
  }

  variantIdFor(box) {
    const select = this.querySelector(`[data-bundle-variant-for="${box.id}"]`);
    return select ? select.value : box.dataset.variantId;
  }

  priceFor(box) {
    const select = this.querySelector(`[data-bundle-variant-for="${box.id}"]`);
    if (!select) return Number(box.dataset.price);
    const option = select.selectedOptions[0];
    return Number(option?.dataset.price || box.dataset.price);
  }

  refresh() {
    if (!this.total) return;

    /* The running total answers "what will I pay when I add this lot", which
       is a question only the deferred mode leaves open. In instant mode the
       extras are already in the cart and the product itself is not, so the
       sum would describe nothing that exists. The cart total is the truth. */
    if (this.instant) {
      this.total.hidden = true;
      return;
    }

    const checked = Array.from(this.querySelectorAll('[data-bundle-item]:checked'));

    if (!checked.length) {
      this.total.hidden = true;
      return;
    }

    const cents = checked.reduce(
      (sum, box) => sum + this.priceFor(box),
      Number(this.dataset.basePrice)
    );

    this.total.hidden = false;
    this.totalValue.textContent = this.money(cents);
  }

  /* Formats using the shop's own locale and currency rather than guessing. */
  money(cents) {
    const currency = window.Shopify?.currency?.active || 'EUR';
    const locale = document.documentElement.lang || 'en';

    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }
}

if (!customElements.get('buy-it-with')) {
  customElements.define('buy-it-with', BuyItWith);
}
